import { GAMMA_MEDIUM_CHAIN } from "./gamma";
import { pisteLabel } from "./piste-labels";
export { pisteLabel } from "./piste-labels";
import { summariseDailyQuality } from "./hourly-windows";
// Piste skiing hourly scoring - purely multiplicative model (v7)
//
// hourly_score = 100 × F_vis × F_surface × F_wind × F_comfort × F_precip
//                    × F_depth × F_groom × F_traffic
//
// Each factor is a continuous multiplier in [~0.03, ~1.05]. No hard caps, no
// additive components, no step functions. Catastrophic conditions produce
// near-zero scores naturally through the multiplication chain.
//
// v6 changes:
//   - Surface quality is refreeze-aware: overnight minimum temperature
//     determines how well groomed corduroy holds against morning warmth.
//     Spring morning +1°C with overnight -8°C = perfect corduroy, not slush.
//   - Snowfall triple-charge removed: the precipitation factor no longer
//     penalises snow (rain still penalised). Visibility and grooming
//     capture the two real effects (can't see, moguls forming).
//   - Pre-4am snowfall is beneficial (piste bashers groom it in).
//     Post-4am accumulation degrades surface immediately (no offset).
//   - Wind centre shifted to 45 km/h (35 km/h sustained = lifts run).
//
// v7 changes:
//   - Grooming degradation is residence-time weighted: each post-4am
//     snowfall increment is penalised by how long it has been SKIED
//     (chop -> piles -> moguls form under traffic over hours), not by
//     clock hour. 3cm fallen at dawn and skied for 8 hours is now worse
//     than 3cm of fresh chop that just fell.
//   - Surface softening is driven by temperature AND direct sunlight:
//     strong spring sun erodes the refreeze shield faster, so a sunny
//     +3°C afternoon slushes out while an overcast +3°C day holds firm.
//   - Skier-traffic wear: a small monotonic decay across the ski day so
//     first-lifts corduroy is the best surface even on a constant cold
//     bluebird; by late afternoon the piste is scraped and chopped.
//   - Warm-side recalibration: floor raised 0.12 -> 0.30 (a +7°C groomed
//     run is slushy but skiable, not catastrophic), Gaussian widened, and
//     shield decay slowed so spring corn survives into early afternoon.
//   - Legacy single-hour shim now threads overnight minimum (refreeze
//     credit) and an approximate grooming load into the synthetic hour,
//     so the headline badge and 7-day forecast inherit v7 behaviour.
//   - Fog fix: the weather-code visibility floor only applies when the
//     sky state corroborates the code (direct sun reaching the ground or
//     little low cloud), so genuine fog miscoded as clear is no longer
//     erased by the clear-sky estimate.
//   - Cold side softened: -22°C corduroy is excellent snow (hard, fast,
//     grippy); the cold penalty lives mostly in the comfort factor, which
//     skiers mitigate by wrapping up warm.
//   - Packed-powder window: a few cm of fresh skied for under ~1.5h earns
//     a small capped bonus that fades as chop load builds, so a brief
//     refresh skis like hero snow while sustained snowfall still degrades
//     into moguls.
//   - Evening refreeze realism: cold does not mean icy (no liquid water
//     at -22°C); ice is a melt-freeze artefact. Once the surface has
//     slushed out, the day-context surface factor never recovers within
//     the same ski day - falling late-day temps refreeze slush into
//     crud/ice, and only the overnight bashers reset it. The refreeze
//     shield also decays on the actual cumulative hourly warming rather
//     than current-temp × elapsed-hours.
//   - Elevation bands: base/mid/summit scores derived from the SAME hourly
//     dataset via lapse-rate adjustment (0.65°C/100m) with rain/snow
//     re-partitioning against the absolute freezing level, and wind scaled
//     with elevation (exposure/free-air profile, BAND_WIND_PER_M) - zero
//     extra fetches, zero extra storage. Captures "slush at base, winter up
//     top". Snow depth is the one field that cannot be lapse-transported; the
//     depth profile (freezing-level haircut + multi-band interpolation, see
//     snow-depth-profile.ts) handles it downstream instead.
//
// Daily score = (0.3 × best 4 consecutive ski-hours avg + 0.7 × full ski-day avg) / 10
// (Deliberately day-weighted: piste skiing is an all-day activity, not a session sport.)

import type { NormalisedHour, WeatherHourly, DailySources } from "./daily-obs-types";
import { composeScore, defineChain } from "./compose-score";
import { reserveTopBand, PISTE_TOP_RESERVE_K } from "./gamma";
import type { TreelineTerrain } from "./offpiste-scoring-v5";
// Phase-2A: the snow-depth profile owns the freezing-level depth haircut (Method
// A) so the headline and trail-scoring share one source of truth. Runtime-only
// import; the snow-depth-profile <-> piste-scoring cycle never reads across at
// module-init time.
import {
  methodADepthRetention,
  naturalDepthAtElevation,
  bandGradientUsable,
  bandAnchorsAtHour,
  clampHourSnowDepth,
  clampHoursSnowDepth,
  clampPhysicalSnowDepthM,
  DEPTH_HAIRCUT_SPAN_M,
  DEPTH_HAIRCUT_FLOOR,
  DEFAULT_B1_PARAMS,
  type B1Params,
  type DepthAnchors,
  type BandAnchors,
} from "./snow-depth-profile";

// Piste's chain, declared as data (tracker P3-19); classes are enforced by
// composeScore.
// Gamma derived from the 8 declared factors (MEDIUM) under the Phase 6
// all-or-none policy (tracker P6-1, audit 90.3 #4): piste carried the exact
// chain-length pathology gamma fixes. The 0-100 scale constant is applied
// outside composeScore so gamma compresses the 0-1 quality product.
export const PISTE_CHAIN = defineChain({
  sport: "piste",
  gamma: GAMMA_MEDIUM_CHAIN, // Pinned calibration; factor-list refactors must not change the scale.
  factors: [
    { key: "visibility", class: "driver", note: "ski flat light: the documented tier migration" },
    { key: "surface", class: "driver" },
    { key: "wind", class: "driver" },
    { key: "comfort", class: "driver", note: "P7-6 removed the cold-side gear-mitigated comfort charge; remaining warm-side dent is slush discomfort, distinct from surface softening" },
    { key: "precip", class: "driver" },
    { key: "depth", class: "driver", note: "base-depth coverage is quality; estimated fallback depth is display confidence, not a score discount (P7-12)" },
    { key: "grooming", class: "driver" },
    { key: "traffic", class: "driver", note: "skier-traffic surface wear: a mechanistic quality factor (11 B3 NO CHANGE), tier-migrated, not a floored enjoyment modifier (P7-1)" },
  ],
});

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type PisteHourlyConfig = {
  adequateBaseCm: number;
  fullBaseCm: number;
  altitudeM: number;
  fallbackDepthM?: number | null;
  /**
   * Crowd multiplier on skier-traffic effects (wear rate and per-hour chop
   * accumulation). 1 = normal; holiday weeks run 1.5 (half-term, penalty
   * 0.5) to 1.7 (Christmas, penalty 0.7), derived from busy_periods.
   */
  crowdFactor?: number;
  /**
   * Skiable-forest classification (`condition_locations.treeline_terrain`), the
   * same column the off-piste scorer reads. Drives the P7-18 storm-day treeline
   * relief: extensive/limited resorts keep contrast while it dumps. NULL/absent
   * = above-treeline or unclassified, the safe default (no relief).
   */
  treelineTerrain?: TreelineTerrain | null;
};

export type PisteFactorDetail = {
  key: string;
  label: string;
  value: number;
  input: string;
};

export type PisteSafetyIndicator = {
  key: string;
  label: string;
  severity: "green" | "amber" | "red";
  detail?: string;
};

export type PisteHourlyResult = {
  score: number;
  factors: PisteFactorDetail[];
  safety: PisteSafetyIndicator[];
};

export type PisteFactorMeans = {
  visibility: number;
  surface: number;
  wind: number;
  comfort: number;
  precipitation: number;
  depth: number;
  grooming: number;
  traffic: number;
};

export type SkiDepthGate = "pass" | "marginal" | "fail" | "unknown";

export type PisteDailyResult = {
  score: number;
  label: string;
  bestWindowAvg: number;
  daylightAvg: number;
  hourlyScores: { hour: number; score: number; isDay: boolean }[];
  /** Mean factor values across ski-day hours (08-18); absent rows count as 1. */
  factorMeans: PisteFactorMeans;
  depthGate: SkiDepthGate;
  safety: PisteSafetyIndicator[];
};

function pisteDepthGate(
  hasDepthEvidence: boolean,
  factor: number,
): SkiDepthGate {
  if (!hasDepthEvidence) return "unknown";
  if (factor < 0.35) return "fail";
  if (factor < 0.95) return "marginal";
  return "pass";
}

// ---------------------------------------------------------------------------
// Constants - every tuning knob is named, not inlined
// ---------------------------------------------------------------------------

// F1: Visibility & light
const VIS_CENTER_M = 2500;
const VIS_STEEPNESS = 1.2;
const VIS_FLOOR = 0.03;
// The weather-code visibility floor (clear-day reanalysis fix) only
// applies when low cloud is below this fraction or direct sun reaches
// the ground - genuine fog miscoded as clear keeps its measured vis.
const VIS_FLOOR_LOW_CLOUD_MAX = 0.40;
// Flat light (diffuse overcast) flattens snow-surface contrast: a real but
// bounded quality dent for a competent skier (P7-17 softened 0.30 -> 0.25 so
// the residual single charge lands the grey-day twin inside the audit's
// 7.5-8.0 band; light STAYS a driver, it is not floored as a modifier).
const FLAT_LIGHT_MAX_PENALTY = 0.25;
const FLAT_LIGHT_CLOUD_THRESHOLD = 0.30;
const DIRECT_RAD_SUN_THRESHOLD_WM2 = 50;
const SUNSHINE_BONUS_MAX = 0.05;
const SUNSHINE_RAD_THRESHOLD_WM2 = 200;
const SUNSHINE_RAD_RANGE_WM2 = 500;

// P7-18 (audit 11 B4): storm-day treeline relief, mirroring the off-piste
// sibling. At a treed resort trees keep snow-surface contrast while it dumps,
// so an active-snowfall hour's visibility floors toward the SAME 0.48-0.65
// target the off-piste scorer uses. The off-piste floor eases on 48h fresh
// accumulation; piste's visibilityFactor is per-hour, so the per-hour snowfall
// RATE is the analogue: it eases in from a trace flurry to steady snow and the
// floor target scales with the rate. Fog still wins (trees do not fix fog) and
// open/unclassified resorts get nothing (treelineStrength 0).
const TREELINE_STORM_FLOOR_MIN = 0.48;
const TREELINE_STORM_FLOOR_MAX = 0.65;
const TREELINE_STORM_EASE_START_CMH = 0.3;
const TREELINE_STORM_EASE_FULL_CMH = 1.5;
const TREELINE_STORM_RATE_MIN_CMH = 1.0;
const TREELINE_STORM_RATE_MAX_CMH = 4.0;

// F2: Surface quality (refreeze-aware, solar-aware)
const SURFACE_SWEET_LOW_C = -8;
const SURFACE_SWEET_HIGH_C = -3;
const SURFACE_WARM_SIGMA_SQ = 50;
// Cold snow is excellent snow: -22°C corduroy is hard, fast and grippy.
// The wide sigma keeps the surface penalty gentle; the human cost of
// deep cold lives in the comfort factor (mitigated by wrapping up warm).
const SURFACE_COLD_SIGMA_SQ = 800;
const SURFACE_WARM_FLOOR = 0.30;
const SURFACE_COLD_FLOOR = 0.20;
const REFREEZE_THRESHOLD_C = -1;
const REFREEZE_STRENGTH_DIVISOR = 8;
const REFREEZE_WARMING_RATE = 0.028;
// Direct sun softens the surface independent of air temperature: ~400 W/m²
// of direct radiation behaves like +1°C of extra warming, capped at +2°C.
const SOLAR_SOFTEN_DIVISOR_WM2 = 400;
const SOLAR_SOFTEN_MAX_C = 2;
// Once the warm-side surface factor drops below this, the surface has
// genuinely slushed out - later hours that day can never score above the
// running minimum (refrozen slush is crud/ice, not fresh corduroy).
const REFREEZE_NO_RECOVERY_THRESHOLD = 0.6;

// F3: Wind
const WIND_SUSTAINED_WEIGHT = 0.6;
const WIND_GUST_WEIGHT = 0.4;
const WIND_CENTER_KMH = 45;
const WIND_SIGMOID_SCALE = 9;
const WIND_FLOOR = 0.05;

// F4: Comfort (actual temperature, NOT apparent - wind has its own factor).
// Warm-side only since P7-6 (audit 11 V1): cold is represented once in the
// surface-quality factor and gear-mitigated cold comfort is excluded (clause 4),
// so only genuine warmth (above +5C) dents this factor. Above +15C warrants a
// meaningful penalty (slush discomfort distinct from the surface slush effect).
const COMFORT_SWEET_HIGH_C = 5;
const COMFORT_WARM_DIVISOR = 1500;
const COMFORT_WARM_FLOOR = 0.80;

// F5: Precipitation
const RAIN_AT_ALT_FLOOR = 0.03;
const RAIN_AT_ALT_RANGE = 0.12;
const RAIN_DECAY_PER_MM = 0.5;
const FREEZING_RAIN_MULT = 0.05;
// P7-14 removed THUNDERSTORM_MULT (the wc >= 95 in-score 0.05 return). Lightning
// is a safety flag, not a quality multiplier: a storm hour scores through the
// existing rain/visibility/wind factors and computeHourlySafety's red flag.

// F6: Snow depth gate
const DEPTH_SIGMOID_STEEPNESS = 6;
const DEPTH_CENTER_RATIO = 0.5;
const DEPTH_FLOOR = 0.05;
// F7: Post-grooming snowfall accumulation (bashers finish ~4am).
// Residence-time model: each cm pays an immediate chop cost plus an
// accumulating cost for every hour it has been skied since lifts opened.
// The 4am cut-off is operations-backed: evening shifts groom the bulk of
// the network by 23:00-01:00, but on snow nights resorts run a dedicated
// fresh-snow shift from ~04:00 so overnight accumulation is combed into
// corduroy with time to refreeze before the ~08:30 open. Snow falling
// after ~4am almost never gets a pass. Traffic-weighted (front pistes get
// the dawn pass and carry most skier-days), 04:00 is the right single
// cut-off; snow in the 04:00 hour itself is conservatively counted as
// post-grooming.
const GROOMING_END_HOUR = 4;
const GROOM_BASE_PER_CM = 0.02;
const GROOM_RATE_PER_CM_HOUR = 0.005;
const GROOMING_FLOOR = 0.50;
// Legacy fallback: when a caller passes only a cumulative cm total
// (no per-hour timing), assume ~4 skied hours of average residence.
const GROOM_FALLBACK_RESIDENCE_H = 4;

// Packed-powder window: a few cm of fresh on groomed corduroy, skied for
// under ~1.5h, is widely loved - a small capped bonus, gated by the
// accumulated chop load so sustained snowfall (which becomes moguls)
// strangles the bonus within 2-3 hours while the penalty keeps growing.
const PACKED_POWDER_MAX_BONUS = 0.05;
const PACKED_POWDER_PEAK_CM = 3;
const PACKED_POWDER_SIGMA_SQ = 6;
const PACKED_POWDER_FRESH_WINDOW_H = 1.5;
const PACKED_POWDER_LOAD_CUTOFF = 0.25;

// F8: Skier-traffic wear - first-lifts corduroy is the best surface of
// the day; the piste is progressively scraped and chopped under traffic
// even with no new snowfall. Crowds (holiday weeks) accelerate both the
// wear rate and the chop accumulation.
const TRAFFIC_WEAR_PER_HOUR = 0.01;
const TRAFFIC_WEAR_FLOOR = 0.82;
const CROWD_FACTOR_MIN = 1;
const CROWD_FACTOR_MAX = 2;

// Elevation bands - derived from the fetched hourly dataset via lapse-rate
// adjustment; the freezing level is an absolute altitude so the rain/snow
// line per band emerges naturally. Wind strengthens with elevation
// (exposure + free-air profile): ±0.04%/m, so a summit 600m above the
// fetch point reads ~24% windier and a sheltered base reads calmer.
const LAPSE_RATE_C_PER_M = 0.0065;
const BAND_RAIN_SPLIT_C = 1.5;
const BAND_SNOW_SPLIT_C = 0.5;
const BAND_SNOW_CM_PER_MM = 0.7;
const BAND_WIND_PER_M = 0.0004;
const BAND_WIND_SCALE_MIN = 0.7;
const BAND_WIND_SCALE_MAX = 1.4;

// Daily rollup
const BEST_WINDOW_HOURS = 4;
const BEST_WINDOW_WEIGHT = 0.3;

const SKI_DAY_START_HOUR = 8;
const SKI_DAY_END_HOUR = 18;

// Safety thresholds
const COLD_AMBER_C = -15;
const COLD_RED_C = -25;
const WIND_AMBER_KMH = 50;
const WIND_RED_KMH = 75;
const CAPE_AMBER_JKG = 500;
const CAPE_RED_JKG = 1000;
const UV_AMBER = 8;
const AVALANCHE_AMBER = 3;
const AVALANCHE_RED = 4;

// ---------------------------------------------------------------------------
// Math helpers
// ---------------------------------------------------------------------------

function sigmoid(x: number): number {
  return 1 / (1 + Math.exp(-x));
}

function clampN(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v));
}

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

function round3(n: number): number {
  return Math.round(n * 1000) / 1000;
}

// ---------------------------------------------------------------------------
// F1: Visibility & light quality
// ---------------------------------------------------------------------------

const WC_VIS_ESTIMATES: Partial<Record<number, number>> = {
  0: 50000, 1: 40000, 2: 30000, 3: 15000,
  45: 200, 48: 150,
  51: 5000, 53: 3000, 55: 1500,
  56: 1500, 57: 1200,
  61: 4000, 63: 2000, 65: 800,
  66: 2000, 67: 1000,
  71: 2500, 73: 1200, 75: 500,
  77: 3000,
  80: 4000, 81: 2500, 82: 800,
  85: 2000, 86: 700,
  95: 1000, 96: 800, 99: 500,
};

function visLogistic(visM: number): number {
  const logVis = Math.log(Math.max(visM, 50));
  const logCenter = Math.log(VIS_CENTER_M);
  return sigmoid(VIS_STEEPNESS * (logVis - logCenter));
}

function estimateVisFromCode(wc: number, cloudPct: number): number {
  const estVis = WC_VIS_ESTIMATES[wc];
  if (estVis != null) return visLogistic(estVis);
  const visMFromCloud = 50000 * Math.exp(-2 * (cloudPct / 100));
  return visLogistic(visMFromCloud);
}

function lerpN(x: number, x0: number, x1: number, y0: number, y1: number): number {
  if (x1 === x0) return y0;
  return y0 + (y1 - y0) * clampN((x - x0) / (x1 - x0), 0, 1);
}

/** Treed-terrain weight, mirroring the off-piste sibling: extensive 1.0, limited 0.5, else 0. */
function pisteTreelineWeight(terrain: TreelineTerrain | null | undefined): number {
  if (terrain === "extensive") return 1.0;
  if (terrain === "limited") return 0.5;
  return 0;
}

function isPisteSnowfallCode(wc: number): boolean {
  return (wc >= 71 && wc <= 77) || wc === 85 || wc === 86;
}

function isPisteFogCode(wc: number): boolean {
  return wc === 45 || wc === 48;
}

/**
 * Per-hour snowfall RATE in cm/h: the measured rate when present, else a
 * weather-code intensity estimate (heavy 75/77, moderate 73/86, light 71/85)
 * so the treeline floor still fires when snowfall_cm is absent but the code
 * says snow. Mirrors the off-piste scorer's code-based intensity buckets.
 */
function snowfallRateCmH(w: WeatherHourly): number {
  const measured = w.snowfall_cm ?? 0;
  if (measured > 0) return measured;
  const wc = w.weather_code;
  if (wc === 75 || wc === 77) return 3.0;
  if (wc === 73 || wc === 86) return 1.5;
  return 0.8;
}

function visibilityFactor(w: WeatherHourly, treelineStrength = 0): number {
  const wcEst = estimateVisFromCode(w.weather_code, w.cloud_cover_pct);
  const lowCloud = (w.cloud_cover_low_pct ?? w.cloud_cover_pct) / 100;
  const directRad = w.direct_radiation_wm2 ?? (w.is_day ? 200 : 0);
  const noDirectSun = directRad < DIRECT_RAD_SUN_THRESHOLD_WM2;

  let visDist: number;
  if (w.visibility_m != null && w.visibility_m > 0) {
    const measured = visLogistic(w.visibility_m);
    // Open-Meteo reanalysis visibility at mountain elevations can be
    // unrealistically low on clear days; use the weather-code estimate
    // as a floor - but only when the sky state corroborates the code
    // (direct sun reaching the ground, or little low cloud). A genuinely
    // foggy hour miscoded as clear keeps its measured visibility.
    const skyCorroborated = !noDirectSun || lowCloud <= VIS_FLOOR_LOW_CLOUD_MAX;
    visDist = skyCorroborated ? Math.max(measured, wcEst) : measured;
  } else {
    visDist = wcEst;
  }

  // Flat-light contrast level keyed on the SAME overcast deck (no direct sun,
  // high low cloud): 1.0 = readable contrast, < 1 = diffuse flat light.
  let contrastLevel = 1.0;
  if (noDirectSun && lowCloud > FLAT_LIGHT_CLOUD_THRESHOLD) {
    const excess =
      (lowCloud - FLAT_LIGHT_CLOUD_THRESHOLD) /
      (1 - FLAT_LIGHT_CLOUD_THRESHOLD);
    contrastLevel = 1 - FLAT_LIGHT_MAX_PENALTY * Math.pow(excess, 1.2);
  }

  let sunBonus = 1.0;
  if (w.is_day && directRad > SUNSHINE_RAD_THRESHOLD_WM2 && visDist > 0.7) {
    const frac = Math.min(
      1,
      (directRad - SUNSHINE_RAD_THRESHOLD_WM2) / SUNSHINE_RAD_RANGE_WM2,
    );
    sunBonus = 1 + SUNSHINE_BONUS_MAX * frac;
  }

  // P7-17 (audit 11 B2, the Niseko question): sight-distance and flat-light
  // both price how well you can read the terrain, and on one overcast deck they
  // are the SAME cause. Price the grey sky ONCE by taking the worse of the two
  // (min), not multiplying them. On a snowfall day the snow-reduced measured
  // vis dominates and flat light no longer piles a second charge on the same
  // sky; on a clear-but-grey day (good distance, flat light) the contrast term
  // dominates and the modest distance haircut does not double-charge. True
  // whiteout still floors at VIS_FLOOR.
  let visQuality = Math.min(visDist, contrastLevel);

  // P7-18 (audit 11 B4): storm-day treeline relief. While it is dumping at a
  // treed resort, trees hold the snow-surface contrast, so the visibility
  // quality floors toward the off-piste sibling's 0.48-0.65 target, eased in by
  // the snowfall rate and scaled by the treed degree. Fog still wins (trees do
  // not fix fog); lulls and open/unclassified resorts get nothing.
  if (
    treelineStrength > 0 &&
    isPisteSnowfallCode(w.weather_code) &&
    !isPisteFogCode(w.weather_code)
  ) {
    const rate = snowfallRateCmH(w);
    if (rate > TREELINE_STORM_EASE_START_CMH) {
      const ease = lerpN(rate, TREELINE_STORM_EASE_START_CMH, TREELINE_STORM_EASE_FULL_CMH, 0, 1);
      const floorTarget = lerpN(
        rate,
        TREELINE_STORM_RATE_MIN_CMH,
        TREELINE_STORM_RATE_MAX_CMH,
        TREELINE_STORM_FLOOR_MIN,
        TREELINE_STORM_FLOOR_MAX,
      );
      visQuality += Math.max(0, floorTarget - visQuality) * ease * treelineStrength;
    }
  }

  return clampN(visQuality * sunBonus, VIS_FLOOR, 1.05);
}

// ---------------------------------------------------------------------------
// F2: Surface quality (refreeze-aware, solar-aware)
//
// Cold side and sweet spot are unchanged. On the warm side, an overnight
// freeze creates a "refreeze shield" that keeps groomed corduroy firm
// through the spring morning even when air temperature is slightly above
// zero. The shield decays with (above-zero temp + solar boost) × (hours
// since ski-day start), producing the realistic morning-to-afternoon
// transition that ski instructors recognise. Direct sun softens the
// surface independent of air temperature: a sunny +3°C slope slushes out
// while an overcast +3°C slope holds firm corduroy.
// ---------------------------------------------------------------------------

function solarSofteningBoostC(directRadWm2: number): number {
  return Math.min(
    SOLAR_SOFTEN_MAX_C,
    Math.max(0, directRadWm2) / SOLAR_SOFTEN_DIVISOR_WM2,
  );
}

function surfaceQualityFactor(
  tempC: number,
  hour: number,
  overnightMinC: number | null,
  directRadWm2: number = 0,
  warmingLoadOverride?: number,
): number {
  if (tempC >= SURFACE_SWEET_LOW_C && tempC <= SURFACE_SWEET_HIGH_C) return 1.0;

  if (tempC < SURFACE_SWEET_LOW_C) {
    const d = tempC - SURFACE_SWEET_LOW_C;
    return Math.max(SURFACE_COLD_FLOOR, Math.exp(-(d * d) / SURFACE_COLD_SIGMA_SQ));
  }

  const warmDelta = tempC - SURFACE_SWEET_HIGH_C;
  const noRefreeze = Math.max(
    SURFACE_WARM_FLOOR,
    Math.exp(-(warmDelta * warmDelta) / SURFACE_WARM_SIGMA_SQ),
  );

  if (overnightMinC == null || overnightMinC >= REFREEZE_THRESHOLD_C) {
    return noRefreeze;
  }

  const refreezeStrength = clampN(
    -overnightMinC / REFREEZE_STRENGTH_DIVISOR,
    0,
    1,
  );
  // Prefer the actual cumulative hourly warming (from the day context);
  // fall back to current-temp × elapsed-hours for standalone callers.
  const hoursSinceOpen = Math.max(0, hour - SKI_DAY_START_HOUR);
  const warmingLoad =
    warmingLoadOverride ??
    Math.max(0, tempC + solarSofteningBoostC(directRadWm2)) *
      hoursSinceOpen *
      REFREEZE_WARMING_RATE;
  const protection = clampN(refreezeStrength - warmingLoad, 0, 1);

  return protection * 1.0 + (1 - protection) * noRefreeze;
}

// ---------------------------------------------------------------------------
// F3: Wind
// ---------------------------------------------------------------------------

function windFactor(windKmh: number, gustKmh: number): number {
  const effective =
    windKmh * WIND_SUSTAINED_WEIGHT + gustKmh * WIND_GUST_WEIGHT;
  return Math.max(
    WIND_FLOOR,
    sigmoid(-(effective - WIND_CENTER_KMH) / WIND_SIGMOID_SCALE),
  );
}

// ---------------------------------------------------------------------------
// F4: Comfort (thermal)
// ---------------------------------------------------------------------------

// P7-6 (audit 11 V1): cold is represented ONCE, in the surface-quality factor
// (cold corduroy is firm, fast and grippy: surfaceQualityFactor dents it gently
// for genuine snow-surface effect). Gear-mitigated cold comfort is excluded
// (clause 4: locals dress for their climate, so a -22C bluebird day is no longer
// double-charged), and the red cold safety flag (below -25C apparent) carries the
// frostbite warning. So the cold side is neutral; only genuine warmth (slush
// discomfort, distinct from the surface slush penalty) dents the comfort factor.
// Param is actual temp_c (wind has its own factor); renamed from the mislabelled
// apparentTempC (resolves P13-7).
function comfortFactor(tempC: number): number {
  if (tempC <= COMFORT_SWEET_HIGH_C) return 1.0;
  const d = tempC - COMFORT_SWEET_HIGH_C;
  return Math.max(COMFORT_WARM_FLOOR, 1 - (d * d) / COMFORT_WARM_DIVISOR);
}

// ---------------------------------------------------------------------------
// F5: Precipitation type
// ---------------------------------------------------------------------------

function precipitationFactor(w: WeatherHourly, altitudeM: number): number {
  const rainMm = w.rain_mm ?? 0;
  const precipMm = w.precip_mm;
  const snowCm = w.snowfall_cm ?? 0;

  if (precipMm <= 0 && rainMm <= 0 && snowCm <= 0) return 1.0;

  const wc = w.weather_code;

  if (wc === 56 || wc === 57 || wc === 66 || wc === 67) return FREEZING_RAIN_MULT;
  // P7-14 (audit 11 V2, 90.9 #3): no in-score thunderstorm multiplier. A storm
  // hour's rain, visibility and wind are already priced by F1/F3/F5, and
  // computeHourlySafety carries the red lightning flag. Lightning is a danger
  // to flag, not a quality collapse to multiply (clause 3: safety never lowers
  // a score). Piste was the one sport that diverged from the storm unification
  // (methodology 2.2); it now joins it.

  const freezingM = w.freezing_level_m;
  const isRainAtAlt =
    w.temp_c > 1 || (freezingM != null && freezingM > altitudeM + 200);

  if (isRainAtAlt && rainMm > 0) {
    return RAIN_AT_ALT_FLOOR + RAIN_AT_ALT_RANGE * Math.exp(-rainMm * RAIN_DECAY_PER_MM);
  }

  if (snowCm > 0 && !isRainAtAlt) return 1.0;

  return Math.max(0.60, 1 - precipMm * 0.15);
}

// ---------------------------------------------------------------------------
// F6: Snow depth gate
// ---------------------------------------------------------------------------

function snowDepthFactor(
  depthM: number | null,
  adequateCm: number,
  fullCm: number,
  fallbackDepthM?: number | null,
): number | null {
  const effective = depthM ?? fallbackDepthM ?? null;
  if (effective == null) return null;
  if (effective <= 0) return 0;
  const ratio = (effective * 100) / adequateCm;
  if (ratio >= fullCm / adequateCm) return 1.0;
  const gate = Math.max(
    DEPTH_FLOOR,
    sigmoid(DEPTH_SIGMOID_STEEPNESS * (ratio - DEPTH_CENTER_RATIO)),
  );
  return gate;
}

// ---------------------------------------------------------------------------
// F7: Post-grooming snowfall accumulation (residence-time weighted)
//
// Snow that fell before the bashers finish (~4am) is groomed into the
// corduroy and costs nothing. Snow falling after pays an immediate chop
// cost per cm plus an accumulating cost for every hour it has been skied
// (nobody skis it before lifts open at 8). So 3cm fallen at dawn and
// skied all day is fully mogulled by 16:00, while 3cm of fresh chop that
// just fell at 15:00 is barely a penalty - and the penalty for morning
// snow keeps worsening through the afternoon even after it stops snowing.
// ---------------------------------------------------------------------------

export type SnowfallIncrement = { hour: number; cm: number };

export function computeGroomingLoad(
  snowfallByHour: ReadonlyArray<SnowfallIncrement>,
  atHour: number,
  crowdFactor: number = 1,
): number {
  const crowd = clampN(crowdFactor, CROWD_FACTOR_MIN, CROWD_FACTOR_MAX);
  let load = 0;
  for (const { hour: s, cm } of snowfallByHour) {
    if (cm <= 0 || s < GROOMING_END_HOUR || s > atHour) continue;
    const skiedHours = Math.max(0, atHour - Math.max(s, SKI_DAY_START_HOUR));
    load += cm * (GROOM_BASE_PER_CM + skiedHours * GROOM_RATE_PER_CM_HOUR * crowd);
  }
  return load;
}

function groomingFactorFromLoad(load: number): number {
  if (load <= 0) return 1.0;
  return Math.max(GROOMING_FLOOR, Math.exp(-load));
}

/** Post-4am snow still inside the fresh window (skied < ~1.5h). */
export function computeFreshRecentCm(
  snowfallByHour: ReadonlyArray<SnowfallIncrement>,
  atHour: number,
): number {
  let fresh = 0;
  for (const { hour: s, cm } of snowfallByHour) {
    if (cm <= 0 || s < GROOMING_END_HOUR || s > atHour) continue;
    const skiedHours = Math.max(0, atHour - Math.max(s, SKI_DAY_START_HOUR));
    if (skiedHours <= PACKED_POWDER_FRESH_WINDOW_H) fresh += cm;
  }
  return fresh;
}

function packedPowderBonus(load: number, freshRecentCm: number): number {
  if (freshRecentCm <= 0) return 1.0;
  const gate = clampN(1 - load / PACKED_POWDER_LOAD_CUTOFF, 0, 1);
  if (gate <= 0) return 1.0;
  const d = freshRecentCm - PACKED_POWDER_PEAK_CM;
  return (
    1 +
    PACKED_POWDER_MAX_BONUS * Math.exp(-(d * d) / PACKED_POWDER_SIGMA_SQ) * gate
  );
}

// ---------------------------------------------------------------------------
// F8: Skier-traffic wear
// ---------------------------------------------------------------------------

function trafficWearFactor(hour: number, crowdFactor: number = 1): number {
  const crowd = clampN(crowdFactor, CROWD_FACTOR_MIN, CROWD_FACTOR_MAX);
  const hoursSinceOpen = Math.max(0, hour - SKI_DAY_START_HOUR);
  return Math.max(
    TRAFFIC_WEAR_FLOOR,
    1 - hoursSinceOpen * TRAFFIC_WEAR_PER_HOUR * crowd,
  );
}

// ---------------------------------------------------------------------------
// Safety indicators (independent of score)
// ---------------------------------------------------------------------------

function computeHourlySafety(w: WeatherHourly): PisteSafetyIndicator[] {
  const out: PisteSafetyIndicator[] = [];

  if (w.weather_code >= 95) {
    out.push({
      key: "thunderstorm",
      label: "Active thunderstorm",
      severity: "red",
      detail:
        "Lightning danger on exposed terrain and near metal chairlifts. Seek shelter immediately.",
    });
  } else if ((w.cape_jkg ?? 0) > CAPE_RED_JKG) {
    out.push({
      key: "thunderstorm",
      label: "Thunderstorm risk",
      severity: "amber",
      detail: "High convective energy - thunderstorms possible later today.",
    });
  } else if ((w.cape_jkg ?? 0) > CAPE_AMBER_JKG) {
    out.push({
      key: "thunderstorm",
      label: "Thunderstorm possible",
      severity: "amber",
    });
  }

  const apparent = w.apparent_temp_c ?? w.temp_c;
  if (apparent < COLD_RED_C) {
    out.push({
      key: "cold",
      label: "Extreme cold",
      severity: "red",
      detail: `Wind chill ${Math.round(apparent)}°C - frostbite risk on exposed skin within minutes.`,
    });
  } else if (apparent < COLD_AMBER_C) {
    out.push({
      key: "cold",
      label: "Very cold",
      severity: "amber",
      detail: `Wind chill ${Math.round(apparent)}°C - cover all exposed skin.`,
    });
  }

  const gusts = w.wind_gust_10m_kmh;
  if (gusts > WIND_RED_KMH) {
    out.push({
      key: "wind",
      label: "Extreme wind",
      severity: "red",
      detail: `Gusts to ${Math.round(gusts)} km/h - most lifts likely on wind hold.`,
    });
  } else if (gusts > WIND_AMBER_KMH) {
    out.push({
      key: "wind",
      label: "Strong wind",
      severity: "amber",
      detail: `Gusts to ${Math.round(gusts)} km/h - upper lifts may close.`,
    });
  }

  if (w.uv_index >= UV_AMBER) {
    out.push({
      key: "uv",
      label: "Very high UV",
      severity: "amber",
      detail: `UV index ${w.uv_index} - wear sunscreen and goggles. Snow reflection doubles exposure.`,
    });
  }

  return out;
}

function computeAvalancheSafety(
  dailySources?: DailySources,
): PisteSafetyIndicator[] {
  const av = dailySources?.avalanche;
  if (!av?.danger_level) return [];

  if (av.danger_level >= AVALANCHE_RED) {
    return [
      {
        key: "avalanche",
        label: `Avalanche danger ${av.danger_level}/5`,
        severity: "red",
        detail:
          "Stay on marked and open pistes only. Always check your national avalanche bulletin.",
      },
    ];
  }
  if (av.danger_level >= AVALANCHE_AMBER) {
    return [
      {
        key: "avalanche",
        label: `Avalanche danger ${av.danger_level}/5`,
        severity: "amber",
        detail:
          "Exercise caution near piste boundaries. Always check your national avalanche bulletin.",
      },
    ];
  }
  return [];
}

// ---------------------------------------------------------------------------
// Format helpers
// ---------------------------------------------------------------------------

function fmtVis(w: WeatherHourly): string {
  if (w.visibility_m != null) return `${(w.visibility_m / 1000).toFixed(1)}km`;
  return `${w.cloud_cover_pct}% cloud`;
}

function fmtWind(w: WeatherHourly): string {
  return `${w.wind_speed_10m_kmh.toFixed(0)} (gusts ${w.wind_gust_10m_kmh.toFixed(0)}) km/h`;
}

function fmtComfort(w: WeatherHourly): string {
  return `${w.temp_c.toFixed(0)}°C`;
}

function fmtPrecip(w: WeatherHourly): string {
  if (w.precip_mm <= 0) return "None";
  if ((w.rain_mm ?? 0) > 0) return `Rain ${(w.rain_mm ?? 0).toFixed(1)}mm`;
  if ((w.snowfall_cm ?? 0) > 0)
    return `Snow ${(w.snowfall_cm ?? 0).toFixed(1)}cm`;
  return `${w.precip_mm.toFixed(1)}mm`;
}

function fmtDepth(w: WeatherHourly, fallbackDepthM?: number | null): string {
  // Clamp glacier-firn sentinels so the displayed base depth is never "3333cm".
  const depthM = clampPhysicalSnowDepthM(w.snow_depth_m);
  if (depthM == null && fallbackDepthM != null) {
    return `Estimated ${Math.round((clampPhysicalSnowDepthM(fallbackDepthM) ?? 0) * 100)}cm`;
  }
  if (depthM == null) return "Unknown";
  return `${Math.round(depthM * 100)}cm`;
}

// ---------------------------------------------------------------------------
// Shared per-day context - single source of truth for the overnight minimum
// and the residence-time grooming load, so the daily rollup, the UI hourly
// chart and the visualiser tool can never diverge.
// ---------------------------------------------------------------------------

export type PisteHourContext = {
  /** Explicit prepared-cover estimate. Replaces the legacy centimetre factor. */
  coverFactor?: number;
  /** Cumulative post-4am snowfall up to this hour (display only). */
  postGroomingSnowCm?: number;
  /** Residence-time-weighted grooming exponent (drives the factor). */
  groomingLoad?: number;
  /** Post-4am snow skied for under ~1.5h - packed-powder bonus input. */
  freshRecentCm?: number;
  /**
   * Day-sequenced surface factor: cumulative hourly warming load plus the
   * no-recovery rule (refrozen slush is crud/ice until the bashers reset
   * it overnight). When absent the hourly scorer falls back to the
   * instantaneous estimate.
   */
  surfaceFactor?: number;
  overnightMinC?: number;
};

export function buildPisteDayContext(
  hours: NormalisedHour[],
  opts?: { crowdFactor?: number },
): {
  overnightMinC: number | null;
  contextForHour: (hour: number) => PisteHourContext;
} {
  const crowdFactor = opts?.crowdFactor ?? 1;
  const sorted = [...hours].sort((a, b) => a.hour - b.hour);

  const overnightHours = sorted.filter((h) => h.hour <= 6);
  const overnightMinC =
    overnightHours.length > 0
      ? Math.min(...overnightHours.map((h) => h.weather.temp_c))
      : null;

  const snowfallByHour: SnowfallIncrement[] = sorted
    .map((h) => ({ hour: h.hour, cm: h.weather.snowfall_cm ?? 0 }))
    .filter((s) => s.cm > 0);

  // Surface factor sequence: the shield decays on the ACTUAL cumulative
  // hourly warming (temp + solar per hour), and once the surface has
  // slushed out it never recovers within the ski day - falling evening
  // temps refreeze slush into crud/ice, not corduroy.
  const surfaceFactorByHour = new Map<number, number>();
  let cumWarmingLoad = 0;
  let noRecoveryCap: number | null = null;
  for (const h of sorted) {
    const t = h.weather.temp_c;
    const rad = h.weather.direct_radiation_wm2 ?? 0;
    const skiing = h.hour >= SKI_DAY_START_HOUR;

    let f = surfaceQualityFactor(
      t,
      h.hour,
      overnightMinC,
      rad,
      skiing ? cumWarmingLoad : 0,
    );
    if (noRecoveryCap != null) f = Math.min(f, noRecoveryCap);

    const slushedOut =
      t > SURFACE_SWEET_HIGH_C && f < REFREEZE_NO_RECOVERY_THRESHOLD;
    if (slushedOut || (noRecoveryCap != null && f < noRecoveryCap)) {
      noRecoveryCap = noRecoveryCap == null ? f : Math.min(noRecoveryCap, f);
    }
    surfaceFactorByHour.set(h.hour, f);

    if (skiing) {
      cumWarmingLoad +=
        Math.max(0, t + solarSofteningBoostC(rad)) * REFREEZE_WARMING_RATE;
    }
  }

  return {
    overnightMinC,
    contextForHour: (hour: number) => {
      let postGroomingSnowCm = 0;
      for (const s of snowfallByHour) {
        if (s.hour >= GROOMING_END_HOUR && s.hour <= hour) {
          postGroomingSnowCm += s.cm;
        }
      }
      return {
        postGroomingSnowCm,
        groomingLoad: computeGroomingLoad(snowfallByHour, hour, crowdFactor),
        freshRecentCm: computeFreshRecentCm(snowfallByHour, hour),
        surfaceFactor: surfaceFactorByHour.get(hour),
        overnightMinC: overnightMinC ?? undefined,
      };
    },
  };
}

// ---------------------------------------------------------------------------
// Hourly scoring
// ---------------------------------------------------------------------------

export function calculatePisteHourly(
  hRaw: NormalisedHour,
  config: PisteHourlyConfig,
  context?: PisteHourContext,
): PisteHourlyResult {
  // Guard the snow-depth gate against glacier-firn sentinels (no-op when physical).
  const h = clampHourSnowDepth(hRaw);
  const w = h.weather;

  const fVis = visibilityFactor(w, pisteTreelineWeight(config.treelineTerrain));
  const fSurface =
    context?.surfaceFactor ??
    surfaceQualityFactor(
      w.temp_c,
      h.hour,
      context?.overnightMinC ?? null,
      w.direct_radiation_wm2 ?? 0,
    );
  const fWind = windFactor(w.wind_speed_10m_kmh, w.wind_gust_10m_kmh);
  const fComfort = comfortFactor(w.temp_c);
  const fPrecip = precipitationFactor(w, config.altitudeM);
  const fDepth = context?.coverFactor ?? snowDepthFactor(
    w.snow_depth_m,
    config.adequateBaseCm,
    config.fullBaseCm,
    config.fallbackDepthM,
  );
  // Unknown depth is excluded from the diagnostic product rather than scored
  // as bare ground or adequate cover. The canonical caller publishes no score
  // for this state; the neutral multiplier keeps the non-depth diagnostics
  // inspectable without assigning a depth judgement.
  const diagnosticDepthMultiplier = fDepth ?? 1;
  // Prefer the residence-time load; fall back to an average-residence
  // approximation when a caller only has a cumulative cm total.
  const postSnow = context?.postGroomingSnowCm ?? 0;
  const groomLoad =
    context?.groomingLoad ??
    (postSnow > 0
      ? postSnow *
        (GROOM_BASE_PER_CM + GROOM_FALLBACK_RESIDENCE_H * GROOM_RATE_PER_CM_HOUR)
      : 0);
  const fGroom = Math.min(
    1.05,
    groomingFactorFromLoad(groomLoad) *
      packedPowderBonus(groomLoad, context?.freshRecentCm ?? 0),
  );
  const fTraffic = trafficWearFactor(h.hour, config.crowdFactor ?? 1);

  const raw = 100 * composeScore(PISTE_CHAIN, {
    visibility: fVis,
    surface: fSurface,
    wind: fWind,
    comfort: fComfort,
    precip: fPrecip,
    depth: diagnosticDepthMultiplier,
    grooming: fGroom,
    traffic: fTraffic,
  }).compressed;
  const score = clampN(round1(raw), 0, 100);

  const surfaceInput = context?.overnightMinC != null
    ? `${w.temp_c.toFixed(1)}°C (overnight ${context.overnightMinC.toFixed(0)}°C)`
    : `${w.temp_c.toFixed(1)}°C`;

  const factors: PisteFactorDetail[] = [
    { key: "visibility", label: "Visibility & light", value: round3(fVis), input: fmtVis(w) },
    { key: "surface", label: "Surface quality", value: round3(fSurface), input: surfaceInput },
    { key: "wind", label: "Wind", value: round3(fWind), input: fmtWind(w) },
    { key: "comfort", label: "Comfort", value: round3(fComfort), input: fmtComfort(w) },
    { key: "precipitation", label: "Precipitation", value: round3(fPrecip), input: fmtPrecip(w) },
    {
      key: "depth",
      label: context?.coverFactor === undefined ? "Snow depth" : "Usable snow cover",
      value: round3(diagnosticDepthMultiplier),
      input: context?.coverFactor === undefined ? fmtDepth(w, config.fallbackDepthM) : `${Math.round(context.coverFactor * 100)}% estimated adequacy`,
    },
  ];

  if (fGroom !== 1.0) {
    factors.push({
      key: "grooming",
      label: fGroom > 1.0 ? "Fresh dusting" : "Grooming",
      value: round3(fGroom),
      input: `${postSnow.toFixed(1)}cm since grooming`,
    });
  }

  if (fTraffic < 1.0) {
    factors.push({
      key: "traffic",
      label: "Piste wear",
      value: round3(fTraffic),
      input: `${Math.max(0, h.hour - SKI_DAY_START_HOUR)}h of skier traffic`,
    });
  }

  return { score, factors, safety: computeHourlySafety(w) };
}

// ---------------------------------------------------------------------------
// Daily rollup
// ---------------------------------------------------------------------------

export function calculatePisteDaily(
  hoursRaw: NormalisedHour[],
  config: PisteHourlyConfig,
  dailySources?: DailySources,
): PisteDailyResult {
  // Clamp glacier-firn sentinel depths once, before any per-elevation processing.
  const hours = clampHoursSnowDepth(hoursRaw);
  const { contextForHour } = buildPisteDayContext(hours, {
    crowdFactor: config.crowdFactor,
  });

  const hourlyResults = hours.map((h) => {
    return {
      hour: h.hour,
      result: calculatePisteHourly(h, config, contextForHour(h.hour)),
      isDay: h.weather.is_day,
    };
  });

  const hourlyScores = hourlyResults.map((r) => ({
    hour: r.hour,
    score: r.result.score,
    isDay: r.isDay,
  }));

  const skiHours = hourlyResults.filter(
    (r) => r.isDay && r.hour >= SKI_DAY_START_HOUR && r.hour <= SKI_DAY_END_HOUR,
  );

  const emptyMeans: PisteFactorMeans = {
    visibility: 0, surface: 0, wind: 0, comfort: 0,
    precipitation: 0, depth: 0, grooming: 0, traffic: 0,
  };

  if (skiHours.length === 0) {
    return {
      score: 0,
      label: pisteLabel(0),
      bestWindowAvg: 0,
      daylightAvg: 0,
      hourlyScores,
      factorMeans: emptyMeans,
      depthGate: "unknown",
      safety: [],
    };
  }

  const factorSums: Record<keyof PisteFactorMeans, number> = { ...emptyMeans };
  for (const r of skiHours) {
    const byKey = new Map(r.result.factors.map((f) => [f.key, f.value]));
    for (const key of Object.keys(factorSums) as Array<keyof PisteFactorMeans>) {
      // Conditional rows (grooming, traffic) are omitted when 1.0
      factorSums[key] += byKey.get(key) ?? 1.0;
    }
  }
  const factorMeans = Object.fromEntries(
    (Object.keys(factorSums) as Array<keyof PisteFactorMeans>).map((key) => [
      key,
      round3(factorSums[key] / skiHours.length),
    ]),
  ) as PisteFactorMeans;

  const daylightAvg =
    skiHours.reduce((s, r) => s + r.result.score, 0) / skiHours.length;

  const daily = summariseDailyQuality(skiHours.map((r) => ({
    hour: r.hour, score: r.result.score, isDay: true,
  })), BEST_WINDOW_HOURS, BEST_WINDOW_WEIGHT);
  const bestWindowAvg = daily.bestWindowAvg ?? daily.daylightAvg;
  const dailyRaw = daily.overall;
  // Reserve the top band for exceptional days (R1); 0-8 is untouched.
  const dailyScore = clampN(
    round1(reserveTopBand(dailyRaw / 100, PISTE_TOP_RESERVE_K) * 10),
    0,
    10,
  );

  const allSafety = new Map<string, PisteSafetyIndicator>();
  for (const r of skiHours) {
    for (const s of r.result.safety) {
      const existing = allSafety.get(s.key);
      const ord = { green: 0, amber: 1, red: 2 } as const;
      if (!existing || ord[s.severity] > ord[existing.severity]) {
        allSafety.set(s.key, s);
      }
    }
  }
  for (const s of computeAvalancheSafety(dailySources)) {
    allSafety.set(s.key, s);
  }

  return {
    score: dailyScore,
    label: pisteLabel(dailyScore),
    bestWindowAvg: round1(bestWindowAvg),
    daylightAvg: round1(daylightAvg),
    hourlyScores,
    factorMeans,
    depthGate: pisteDepthGate(
      hours.some((hour) => hour.weather.snow_depth_m !== null) ||
        config.fallbackDepthM !== null &&
        config.fallbackDepthM !== undefined,
      factorMeans.depth,
    ),
    safety: Array.from(allSafety.values()),
  };
}

// ---------------------------------------------------------------------------
// Elevation bands
//
// Base/mid/summit scores derived from the SAME stored hourly dataset:
// temperatures are lapse-adjusted (0.65°C/100m), precipitation phase is
// re-partitioned per band against the band temperature (so "rain at the
// base, snow up top" emerges naturally), and wind is scaled with elevation
// (BAND_WIND_PER_M: summits read windier, sheltered bases calmer).
// Zero extra API fetches, zero extra storage - pure read-time arithmetic.
// Snow depth is the one field not lapse-transportable here (lapseAdjustHour
// carries it through unchanged); the depth profile applies a freezing-level
// haircut and multi-band interpolation downstream (snow-depth-profile.ts).
// ---------------------------------------------------------------------------

const SNOW_TO_RAIN_CODE: Partial<Record<number, number>> = {
  71: 61, 73: 63, 75: 65, 77: 61, 85: 80, 86: 81,
};
const RAIN_TO_SNOW_CODE: Partial<Record<number, number>> = {
  51: 71, 53: 71, 55: 73, 56: 71, 57: 73,
  61: 71, 63: 73, 65: 75, 66: 71, 67: 73,
  80: 85, 81: 86, 82: 86,
};

// Exported for trail-level scoring (lib/conditions/trail-scoring.ts), which
// reuses the exact band transformation per trail. Behaviour unchanged.
export function lapseAdjustHour(h: NormalisedHour, deltaM: number): NormalisedHour {
  const dT = -deltaM * LAPSE_RATE_C_PER_M;
  const windScale = clampN(
    1 + deltaM * BAND_WIND_PER_M,
    BAND_WIND_SCALE_MIN,
    BAND_WIND_SCALE_MAX,
  );
  const w = h.weather;
  const tempC = w.temp_c + dT;

  let rainMm = w.rain_mm;
  let showersMm = w.showers_mm;
  let snowfallCm = w.snowfall_cm;
  let weatherCode = w.weather_code;

  if (w.precip_mm > 0) {
    if (tempC > BAND_RAIN_SPLIT_C && (w.snowfall_cm ?? 0) > 0) {
      // Warmer band: what fell as snow at the reference elevation is rain here
      rainMm = w.precip_mm;
      showersMm = 0;
      snowfallCm = 0;
      weatherCode = SNOW_TO_RAIN_CODE[w.weather_code] ?? w.weather_code;
    } else if (tempC < BAND_SNOW_SPLIT_C && (w.rain_mm ?? 0) + (w.showers_mm ?? 0) > 0) {
      // Colder band: rain at the reference elevation falls as snow here
      snowfallCm = (snowfallCm ?? 0) + ((w.rain_mm ?? 0) + (w.showers_mm ?? 0)) * BAND_SNOW_CM_PER_MM;
      rainMm = 0;
      showersMm = 0;
      weatherCode = RAIN_TO_SNOW_CODE[w.weather_code] ?? w.weather_code;
    }
  }

  return {
    ...h,
    weather: {
      ...w,
      temp_c: tempC,
      apparent_temp_c: w.apparent_temp_c != null ? w.apparent_temp_c + dT : null,
      rain_mm: rainMm,
      showers_mm: showersMm,
      snowfall_cm: snowfallCm,
      weather_code: weatherCode,
      wind_speed_10m_kmh: w.wind_speed_10m_kmh * windScale,
      wind_gust_10m_kmh: w.wind_gust_10m_kmh * windScale,
    },
  };
}

// ---------------------------------------------------------------------------
// Representative whole-resort headline (v7 elevation policy)
//
// The single-elevation headline (calculatePisteDaily over the stored
// elevation_m hours) reports the SUMMIT, which over-represents the lift-served
// resort: elevation_m sits a median +494 m above mid-mountain, so the headline
// reads the coldest, deepest, snowiest band. The representative headline scores
// a lift-served MID band and a SUMMIT band from the SAME stored hourly via the
// lapse transform, and blends them so the lift-served body dominates while high
// terrain keeps a minority credit.
//
// Snow depth cannot be lapse-transported (lapseAdjustHour carries it through
// unchanged), so the MID band applies a freezing-level-aware depth haircut: when
// the 0 °C line sits above mid, the lower snowpack is melting and the
// summit-fetched depth over-states mid coverage, so effective mid depth is
// scaled toward a floor. The SUMMIT band keeps raw depth (summit-fetched depth
// is representative at the summit). This kills the glacier-top false-green
// (Zermatt late-May 8.3 -> honest mid-single-digits) at zero extra fetch cost.
// ---------------------------------------------------------------------------

/** Mid/summit weights for the representative blend (tunable; golden-pinned). */
export const PISTE_MID_WEIGHT = 0.65;
export const PISTE_SUMMIT_WEIGHT = 0.35;
// Effective mid-band snow depth is scaled by a retention factor that falls from
// 1.0 (freezing level at/below mid = full winter, no haircut) toward the floor
// as the 0 °C line rises DEPTH_HAIRCUT_SPAN_M above mid (isothermal/melting). The
// span/floor and the retention live in snow-depth-profile.ts (Method A) so the
// headline, trail-scoring and the B1 profile all read one calibrated source.

export type PisteBands = {
  /** Elevation the stored hourly was fetched at (condition_locations.elevation_m). */
  dataElevM: number;
  /** Lift-served mid-mountain (condition_locations.mid_elevation_m). */
  midElevM: number;
  /** Resort top (condition_locations.elevation_summit_m). */
  summitElevM: number;
};

/**
 * Lapse a stored hour to a band, optionally haircutting its (un-transportable)
 * snow depth. Phase-2A Method A: when the archive returns null for the freezing
 * level (~93% of rows), it is reconstructed from the lapsed band temperature, so
 * a warm-mid thaw deflates the carried summit depth instead of riding through
 * unchanged. A real freezing level still wins where present.
 */
function toBandHour(
  h: NormalisedHour,
  deltaM: number,
  bandElevM: number,
  haircut: boolean,
  depthOverrideM?: number,
): NormalisedHour {
  const lapsed = lapseAdjustHour(h, deltaM);
  // An explicit profile or managed measurement supplies this band's depth, so the
  // per-hour haircut must not be applied a second time.
  if (depthOverrideM != null) {
    return { ...lapsed, weather: { ...lapsed.weather, snow_depth_m: depthOverrideM } };
  }
  const depth = lapsed.weather.snow_depth_m;
  if (!haircut || depth == null) return lapsed;
  const ret = methodADepthRetention(
    lapsed.weather.freezing_level_m,
    lapsed.weather.temp_c,
    bandElevM,
    DEPTH_HAIRCUT_SPAN_M,
    DEPTH_HAIRCUT_FLOOR,
  );
  if (ret >= 1) return lapsed;
  return { ...lapsed, weather: { ...lapsed.weather, snow_depth_m: depth * ret } };
}

/** Median of a non-empty set of independently supplied band anchors. */
function medianN(values: number[]): number {
  const s = [...values].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
}

/**
 * Natural-depth and explicit managed-piste context for the headline.
 * A window of >=2 day-ordered hourly arrays (scored day last) engages Method B1
 * on the mid band; absent, the mid band keeps the per-hour Method A haircut.
 * Operating reports cannot increase numeric depth. Managed measurements are
 * supplied only by E2; E1 operating reports remain non-numeric context.
 */
export type PisteDepthContext = {
  /** Explicit representative managed-piste measurement, supplied by E2 only. */
  managedBaseDepthM?: number;
  /** Day-ordered trailing window (oldest→newest, scored day last). >=2 days engages B1. */
  window?: NormalisedHour[][];
  /** Scored day retained for compatibility with existing callers. */
  mmdd?: string;
  /** Season context retained for caller compatibility; it never creates depth. */
  seasonStart?: string | null;
  seasonEnd?: string | null;
  /**
   * Legacy operating context. It no longer permits a numeric depth uplift.
   */
  adequacyEstablished?: boolean;
  /** B1 constants; defaults to DEFAULT_B1_PARAMS. */
  b1?: B1Params;
  /**
   * Phase-3 multi-band natural snow depths (base/mid/summit + native cell) for
   * this day. When present with >=2 cleaned anchor points, the mid band's natural
   * depth comes from the interpolated profile (no trailing window required) and
   * B1/Method-A become the fallback. Grooming never increases this depth.
   */
  bandAnchors?: BandAnchors;
};

/**
 * Whole-resort piste headline: 0.65·mid + 0.35·summit, both derived from the
 * stored elevation_m hourly via the lapse transform. The mid band carries the
 * displayed hourly detail (the lift-served body people ski); safety is the union
 * of both bands (a summit cold/avalanche flag still counts).
 */
export function calculatePisteDailyRepresentative(
  hoursRaw: NormalisedHour[],
  config: PisteHourlyConfig,
  bands: PisteBands,
  dailySources?: DailySources,
  depthCtx?: PisteDepthContext,
): PisteDailyResult {
  // Clamp glacier-firn sentinels before the high-band anchor + per-band haircut
  // are derived, so the whole multi-band profile is built on a physical depth.
  const hours = clampHoursSnowDepth(hoursRaw);
  const midDelta = bands.midElevM - bands.dataElevM;
  const summitDelta = bands.summitElevM - bands.dataElevM;

  // Phase-3: the mid band's natural depth comes from EITHER the interpolated
  // multi-band profile (>=2 cleaned anchor points; no window needed) OR Method B1
  // (a >=2-day trailing window). The depth is fetched at dataElevM, the trusted
  // anchor for B1 (rho <= 1 deflates BELOW it); naturalDepthAtElevation routes to
  // contemporaneous band interpolation, else B1, else Method A. The summit
  // also uses its own hourly band when available, with raw depth as fallback.
  const fallbackDepthM = clampPhysicalSnowDepthM(config.fallbackDepthM);
  const managedBaseM = clampPhysicalSnowDepthM(depthCtx?.managedBaseDepthM);
  const midHours = hours.map((h) => {
    if (managedBaseM !== null) return toBandHour(h, midDelta, bands.midElevM, false, managedBaseM);
    const hourlyAnchors = bandAnchorsAtHour(depthCtx?.bandAnchors, h.hour);
    const interpAvailable = bandGradientUsable(hourlyAnchors);
    const anchorDepths = hourlyAnchors?.points
      .map(point => clampPhysicalSnowDepthM(point.depthM))
      .filter((depth): depth is number => depth !== null) ?? [];
    const bandFallbackM = anchorDepths.length ? medianN(anchorDepths) : null;
    const highDepthM = h.weather.snow_depth_m ?? fallbackDepthM ?? bandFallbackM;
    if (highDepthM === null && !interpAvailable) {
      return toBandHour(h, midDelta, bands.midElevM, true);
    }
    const elapsed = hours.filter((point) => point.hour <= h.hour);
    const elapsedWindow = depthCtx?.window?.length
      ? [...depthCtx.window.slice(0, -1), elapsed]
      : undefined;
    const anchors: DepthAnchors = {
      dataElevM: bands.dataElevM, anchorElevM: bands.dataElevM,
      highDepthM: highDepthM ?? 0,
    };
    const midDepthM = naturalDepthAtElevation(bands.midElevM, anchors, {
      window: elapsedWindow,
      dayHours: [h],
      b1: depthCtx?.b1 ?? DEFAULT_B1_PARAMS,
      haircutSpanM: DEPTH_HAIRCUT_SPAN_M,
      haircutFloor: DEPTH_HAIRCUT_FLOOR,
      bandAnchors: hourlyAnchors,
    }).depthM;
    // Grooming changes surface quality. Operating status and cold weather do
    // not supply a measured conversion from natural snow to piste centimetres.
    return toBandHour(h, midDelta, bands.midElevM, true, midDepthM);
  });
  const summitHours = hours.map(h => {
    if (managedBaseM !== null) return toBandHour(h, summitDelta, bands.summitElevM, false, managedBaseM);
    const bandAnchors = bandAnchorsAtHour(depthCtx?.bandAnchors, h.hour);
    const summitDepthM = bandGradientUsable(bandAnchors)
      ? naturalDepthAtElevation(bands.summitElevM, {
        dataElevM: bands.dataElevM, anchorElevM: bands.dataElevM,
        highDepthM: h.weather.snow_depth_m ?? 0,
      }, {
        dayHours: [h], bandAnchors,
        haircutSpanM: DEPTH_HAIRCUT_SPAN_M, haircutFloor: DEPTH_HAIRCUT_FLOOR,
      }).depthM : undefined;
    return toBandHour(h, summitDelta, bands.summitElevM, false, summitDepthM);
  });
  const mid = calculatePisteDaily(midHours, { ...config, altitudeM: bands.midElevM }, dailySources);
  const summit = calculatePisteDaily(summitHours, { ...config, altitudeM: bands.summitElevM }, dailySources);
  const score = clampN(
    round1(PISTE_MID_WEIGHT * mid.score + PISTE_SUMMIT_WEIGHT * summit.score),
    0,
    10,
  );
  const ord = { green: 0, amber: 1, red: 2 } as const;
  const safety = new Map<string, PisteSafetyIndicator>();
  for (const s of [...summit.safety, ...mid.safety]) {
    const e = safety.get(s.key);
    if (!e || ord[s.severity] > ord[e.severity]) safety.set(s.key, s);
  }
  return { ...mid, score, label: pisteLabel(score), safety: Array.from(safety.values()) };
}

// ---------------------------------------------------------------------------
// Backward-compat: score from a daily SkiWeatherReading (legacy path)
// ---------------------------------------------------------------------------

export type LegacySkiWeatherReading = {
  tempMaxC: number;
  snowDepthCm: number | null;
  windSpeedKmh: number;
  weatherCode: number;
  precipitationMm: number;
  apparentTempMaxC?: number;
  windGustsMaxKmh?: number;
  cloudCoverPct?: number;
  freezingLevelM?: number | null;
  /** Overnight minimum - enables refreeze credit on warm spring days. */
  tempMinC?: number;
  /** Snowfall hours 0-5 today (only ~1/3 falls after the 4am basher cut). */
  overnightSnowfallCm?: number;
  /** Snowfall hours 6-23 today - degrades the groomed surface. */
  daytimeSnowfallCm?: number;
};

export function scorePisteFromLegacyReading(
  reading: LegacySkiWeatherReading,
  config: PisteHourlyConfig,
): { score: number; label: string; visibilityFactor: number; surfaceFactor: number; windFactor: number; comfortFactor: number; depthFactor: number } {
  const syntheticHour: NormalisedHour = {
    hour: 12,
    weather: {
      temp_c: reading.tempMaxC,
      apparent_temp_c: reading.apparentTempMaxC ?? reading.tempMaxC,
      dew_point_c: null,
      wet_bulb_temp_c: null,
      temp_80m_c: null,
      temp_120m_c: null,
      temp_180m_c: null,
      humidity_pct: null,
      vapour_pressure_deficit_kpa: null,
      precip_mm: reading.precipitationMm / 9,
      rain_mm: reading.tempMaxC > 1 ? reading.precipitationMm / 9 : 0,
      showers_mm: null,
      snowfall_cm: reading.tempMaxC <= 0 ? (reading.precipitationMm / 9) * 0.8 : null,
      snow_depth_m: reading.snowDepthCm === null ? null : reading.snowDepthCm / 100,
      precip_probability_pct: null,
      weather_code: reading.weatherCode,
      cloud_cover_pct: reading.cloudCoverPct ?? 50,
      cloud_cover_low_pct: null,
      cloud_cover_mid_pct: null,
      cloud_cover_high_pct: null,
      sunshine_duration_s: null,
      pressure_msl_hpa: null,
      surface_pressure_hpa: null,
      wind_speed_10m_kmh: reading.windSpeedKmh,
      wind_dir_10m_deg: 0,
      wind_gust_10m_kmh: reading.windGustsMaxKmh ?? reading.windSpeedKmh,
      wind_speed_80m_kmh: null,
      wind_dir_80m_deg: null,
      wind_speed_120m_kmh: null,
      wind_dir_120m_deg: null,
      wind_speed_180m_kmh: null,
      wind_dir_180m_deg: null,
      visibility_m: null,
      uv_index: 0,
      is_day: true,
      shortwave_radiation_wm2: null,
      direct_radiation_wm2: null,
      diffuse_radiation_wm2: null,
      direct_normal_irradiance_wm2: null,
      terrestrial_radiation_wm2: null,
      freezing_level_m: reading.freezingLevelM ?? null,
      cape_jkg: null,
      lifted_index: null,
      convective_inhibition_jkg: null,
      boundary_layer_height_m: null,
      evapotranspiration_mm: null,
      et0_fao_mm: null,
      soil_temp_0cm_c: null,
      soil_temp_6cm_c: null,
      soil_temp_18cm_c: null,
      soil_temp_54cm_c: null,
      soil_moisture_0_1cm: null,
      soil_moisture_1_3cm: null,
      soil_moisture_3_9cm: null,
      soil_moisture_9_27cm: null,
      soil_moisture_27_81cm: null,
    },
    marine: null,
  };

  // Approximate the post-grooming snowfall the day will see by midday:
  // ~1/3 of the overnight window (hours 0-5) falls after the 4am basher
  // cut, and roughly half of the daytime snowfall has fallen by noon.
  // Without the split (forecast days), assume half the day's snow total.
  const hasSplit =
    reading.overnightSnowfallCm != null || reading.daytimeSnowfallCm != null;
  const daySnowEstimateCm =
    reading.tempMaxC <= 0 ? reading.precipitationMm * 0.7 : 0;
  const approxPostGroomCm = hasSplit
    ? (reading.overnightSnowfallCm ?? 0) / 3 + (reading.daytimeSnowfallCm ?? 0) * 0.5
    : daySnowEstimateCm * 0.5;

  const result = calculatePisteHourly(syntheticHour, config, {
    overnightMinC: reading.tempMinC,
    postGroomingSnowCm: approxPostGroomCm,
    groomingLoad:
      approxPostGroomCm > 0
        ? approxPostGroomCm *
          (GROOM_BASE_PER_CM + GROOM_FALLBACK_RESIDENCE_H * GROOM_RATE_PER_CM_HOUR)
        : 0,
  });
  const dailyScore = clampN(round1(result.score / 10), 0, 10);

  const factorMap: Record<string, number> = {};
  for (const f of result.factors) {
    factorMap[f.key] = f.value;
  }

  return {
    score: dailyScore,
    label: pisteLabel(dailyScore),
    visibilityFactor: factorMap["visibility"] ?? 1,
    surfaceFactor: factorMap["surface"] ?? 1,
    windFactor: factorMap["wind"] ?? 1,
    comfortFactor: factorMap["comfort"] ?? 1,
    depthFactor: factorMap["depth"] ?? 1,
  };
}

// Export individual factor functions for testing
export const _factors = {
  visibilityFactor,
  surfaceQualityFactor,
  windFactor,
  comfortFactor,
  precipitationFactor,
  snowDepthFactor,
  computeGroomingLoad,
  groomingFactorFromLoad,
  computeFreshRecentCm,
  packedPowderBonus,
  trafficWearFactor,
} as const;
